import React from 'react';
import Modal from '@ff/ui/Modal';
import Text from '@ff/ui/Text';
import { styled } from '@ff/ui';
import Box from '@ff/ui/Box';
import SelectField from '@ff/ui/Form2/Fields/SelectField';
import InputField from '@ff/ui/Form2/Fields/InputField';
import Form from '@ff/ui/Form2';
import FormRow from '@ff/ui/Form2/FormRow';
import FlexRow from '@ff/ui/FlexRow';
import IconButton from '@ff/ui/IconButton';
import Currency, { currencyNoDecimal } from 'client/v3/components/Currency';
import { acquisitionFeeFormatted, depositFeeFormatted, destinationFee, destinationFeeFormatted, annualMiles, useLeaseContext } from './LeaseContext';

export default function LeaseOptionModal(props) {
  const { vehiclePrice, open, onClose } = props;

  const { selectedOption } = useLeaseContext();
  const { downPaymentFormatted, monthlyFormatted, dueFormatted } = selectedOption;

  return (
    <Modal
      backdrop
      open={open}
      onClose={onClose}
      styleProps={{ borderRadius: '6px' }}
    >
      <Content>
        <IconContainer>
          <IconButton
            icon='ff-icon-close'
            iconProps={{ fontSize: 18 }}
            color='black'
            onClick={onClose}
          />
        </IconContainer>
        <Title>FF Lease Pricing</Title>
        <Subtitle>Pay only a portion of the full price for the term of your lease.</Subtitle>
        <Divider mt='16px' mb='16px' />
        <Row mb='36px'>
          <PriceText>Vehicle Price</PriceText>
          <PriceText><Currency>{vehiclePrice}</Currency></PriceText>
        </Row>
        <PaymentCalculatorForm />
        <Divider mt='8px' mb='8px' />
        <Row>
          <PaymentText fontSize='17px' lineHeight='24px'>Est. Lease Payment</PaymentText>
          <PaymentText fontSize='24px' lineHeight='30px'>{selectedOption.monthlyFormatted} / mo</PaymentText>
        </Row>
        <Terms>
          {`Lease offer is based on the Manufacturer's Suggested Retail Price of ${currencyNoDecimal(vehiclePrice + destinationFee)} including ${destinationFeeFormatted} destination fee.
          Lease offer only available to U.S. customers subject to credit approval and vehicle availability. Lease program is subject to change prior to order confirmation.`}
        </Terms>
        <Terms>
          {`${dueFormatted} due at signing includes your selected down payment (${downPaymentFormatted}), first month's payment before tax (${monthlyFormatted}), and acquisition fee (${acquisitionFeeFormatted}).
          Additional taxes, title, license, and registration fees are due at signing and your monthly payment may be subject to tax. All payments listed are estimates. See lease agreement for complete details.`}
        </Terms>
        <Terms>
          {`Lessee is responsible for insurance and maintenance during the lease term, excess wear and tear as defined in the lease contract, $1.50/mile over ${annualMiles},
          plus disposition fee of ${depositFeeFormatted} at lease end. Late payment and early termination fee apply.`}
        </Terms>
      </Content>
    </Modal>
  );
}

function PaymentCalculatorForm() {
  const { selectedOption, setSelectedOption, leaseOptions } = useLeaseContext();

  const initialValues = {
    downPayment: String(selectedOption.downPayment),
    annualMiles: annualMiles,
    term: '36 Months',
    due: selectedOption.dueFormatted,
  };

  const options = leaseOptions.map((x) => ({
    value: String(x.downPayment),
    text: x.downPaymentFormatted,
  }));

  return (
    <Form formId='lease-form' initialValues={initialValues}>
      <FormRow>
        <SelectField
          name='downPayment'
          label='Down Payment'
          fieldBasis='50%'
          options={options}
          onChange={(event, form) => {
            const found = leaseOptions.find((x) => String(x.downPayment) === event.target.value);
            if (found) {
              setSelectedOption(found);
              form.setFieldValue('due', found.dueFormatted);
            }
          }}
        />
        <InputField name='annualMiles' label='Annual Miles' fieldBasis='50%' disabled />
      </FormRow>
      <FormRow>
        <InputField name='term' label='Term' fieldBasis='50%' disabled />
        <InputField name='due' label='Due at lease signing' fieldBasis='50%' disabled />
      </FormRow>
    </Form>
  )
}

const Content = styled(Box)({
  padding: '40px',
  maxWidth: '809px'
});

const IconContainer = styled(FlexRow)({
  width: '100%',
  flexDirection: 'row-reverse'
});

const Title = styled(Text)({
  fontSize: '24px',
  lineHeight: '28px',
  fontWeight: 500,
  color: 'rgba(23, 26, 32, 1)'
});

const Subtitle = styled(Text)({
  fontSize: '14px',
  lineHeight: '22px',
  marginTop: '16px',
  color: 'rgba(57, 60, 65, 1)'
});

const PriceText = styled(Text)({
  fontSize: '16px',
  lineHeight: '24px',
  color: 'rgba(23, 26, 32, 1)'
});

const PaymentText = styled(Text)({
  fontWeight: 500,
  color: 'rgba(23, 26, 32, 1)'
});

const Terms = styled(Text)({
  fontSize: '12px',
  lineHeight: '20px',
  color: 'rgba(92, 94, 98, 1)',
  marginTop: '12px',
  marginBottom: '12px'
});

const Row = styled(FlexRow)({
  justifyContent: 'space-between',
  alignItems: 'center',
});

const Divider = styled(Box)({
  height: '0.5px',
  width: '100%',
  backgroundColor: 'rgba(172, 172, 172, 1)',
});
